/**
 * Date range filter for the OneDrive Documents search bar.
 * OFF: calendar icon only. ON: selected range + clear. Apply in callout pushes range to parent.
 * From/To accept typed YYYY-MM-DD values; calendars expose month and year pickers.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar,
  Callout,
  DateRangeType,
  DirectionalHint,
  Icon,
} from "@fluentui/react";
import { useTheme } from "../../../theme/OAS365Theme";

const PRESETS = [
  { key: "3-months", label: "Last 3 months", months: 3 },
];

const startOfDay = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const getPresetRange = (preset) => {
  const end = startOfDay(new Date());
  const start = new Date(end);
  if (preset.months) {
    start.setMonth(start.getMonth() - preset.months);
  } else {
    start.setDate(start.getDate() - preset.days);
  }
  return { start, end };
};

const formatDate = (date) =>
  date
    ? new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date)
    : "";

const parseTypedDate = (text) => {
  const trimmed = String(text ?? "").trim();
  const match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 2100) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return startOfDay(date);
};

export function getDefaultDateRange() {
  const preset = PRESETS[0];
  const range = getPresetRange(preset);
  return {
    start: range.start,
    end: range.end,
    preset: preset.key,
    label: preset.label,
  };
}

export default function DateRangeFilter({
  disabled = false,
  enabled = false,
  value = null,
  onApply,
  onEnabledChange,
}) {
  const theme = useTheme();
  const buttonRef = useRef(null);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const applied = value ?? defaultRange;
  const draftDefaults = applied;

  const [isOpen, setIsOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState(draftDefaults.preset ?? "");
  const [draftStart, setDraftStart] = useState(draftDefaults.start ?? null);
  const [draftEnd, setDraftEnd] = useState(draftDefaults.end ?? null);
  const [startText, setStartText] = useState(() => formatDate(draftDefaults.start));
  const [endText, setEndText] = useState(() => formatDate(draftDefaults.end));
  const [startFocused, setStartFocused] = useState(false);
  const [endFocused, setEndFocused] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setDraftPreset(draftDefaults.preset ?? "");
      setDraftStart(draftDefaults.start ?? null);
      setDraftEnd(draftDefaults.end ?? null);
      setStartText(formatDate(draftDefaults.start));
      setEndText(formatDate(draftDefaults.end));
      setStartFocused(false);
      setEndFocused(false);
    }
  }, [draftDefaults.preset, draftDefaults.start, draftDefaults.end, isOpen]);

  useEffect(() => {
    if (!startFocused) setStartText(formatDate(draftStart));
  }, [draftStart, startFocused]);

  useEffect(() => {
    if (!endFocused) setEndText(formatDate(draftEnd));
  }, [draftEnd, endFocused]);

  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
    }
  }, [enabled]);

  const green =
    theme.colors.addovationMainBtnColor ??
    theme.colors.themeDarkColor ??
    "#2f9468";
  /** Match Filters button outline (`addovation30Gray`). */
  const outline = theme.colors.addovation30Gray ?? "#c8c8c8";
  const border = outline;
  const text = theme.colors.fontColor ?? "#252525";
  const background = theme.colors.backgroundColor ?? "#ffffff";

  const appliedLabel =
    applied?.label ||
    (applied?.start && applied?.end
      ? `${formatDate(applied.start)} – ${formatDate(applied.end)}`
      : "Date range");

  const selectPreset = (preset) => {
    const range = getPresetRange(preset);
    setDraftPreset(preset.key);
    setDraftStart(range.start);
    setDraftEnd(range.end);
  };

  const selectStart = (date) => {
    if (!date) return;
    const nextStart = startOfDay(date);
    setDraftPreset("custom");
    setDraftStart(nextStart);
    if (draftEnd && nextStart > draftEnd) setDraftEnd(nextStart);
  };

  const selectEnd = (date) => {
    if (!date) return;
    const nextEnd = startOfDay(date);
    setDraftPreset("custom");
    setDraftEnd(nextEnd);
    if (draftStart && nextEnd < draftStart) setDraftStart(nextEnd);
  };

  const commitTypedDate = (which) => {
    const isStart = which === "start";
    const parsed = parseTypedDate(isStart ? startText : endText);
    if (parsed) {
      if (isStart) selectStart(parsed);
      else selectEnd(parsed);
      return;
    }
    if (isStart) setStartText(formatDate(draftStart));
    else setEndText(formatDate(draftEnd));
  };

  const onTypedDateChange = (which, nextText) => {
    if (which === "start") {
      setStartText(nextText);
      const parsed = parseTypedDate(nextText);
      if (parsed) selectStart(parsed);
      return;
    }
    setEndText(nextText);
    const parsed = parseTypedDate(nextText);
    if (parsed) selectEnd(parsed);
  };

  const apply = () => {
    const preset = PRESETS.find((item) => item.key === draftPreset);
    const next = {
      start: draftStart,
      end: draftEnd,
      preset: draftPreset || "custom",
      label:
        preset?.label ??
        (draftStart && draftEnd
          ? `${formatDate(draftStart)} – ${formatDate(draftEnd)}`
          : "Date range"),
    };
    onApply?.(next);
    setIsOpen(false);
  };

  const clearDraft = () => {
    const today = startOfDay(new Date());
    setDraftPreset("custom");
    setDraftStart(today);
    setDraftEnd(today);
  };

  const turnOff = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(false);
    onEnabledChange?.(false);
  };

  const turnOn = () => {
    if (disabled) return;
    onEnabledChange?.(true);
  };

  const openPicker = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
  };

  const presetButtonStyle = (selected) => ({
    width: "100%",
    minHeight: 38,
    border: 0,
    borderRadius: 0,
    padding: "0 18px",
    textAlign: "left",
    cursor: "pointer",
    color: text,
    background: selected ? `${green}18` : "transparent",
    fontFamily: theme.fonts.regular,
    fontSize: 12,
  });

  const dateField = (label, which, fieldText) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          marginBottom: 7,
          color: theme.colors.addovation50Gray ?? "#666",
          fontFamily: theme.fonts.regular,
          fontSize: 11,
        }}
      >
        {label}
      </div>
      <div
        style={{
          height: 36,
          padding: "0 10px",
          border: `1px solid ${border}`,
          borderRadius: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          boxSizing: "border-box",
          color: text,
          fontFamily: theme.fonts.regular,
          fontSize: 12,
        }}
      >
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="YYYY-MM-DD"
          aria-label={`${label} date. Type year, month, and day as YYYY-MM-DD`}
          value={fieldText}
          onChange={(event) => onTypedDateChange(which, event.target.value)}
          onFocus={() => (which === "start" ? setStartFocused(true) : setEndFocused(true))}
          onBlur={() => {
            commitTypedDate(which);
            if (which === "start") setStartFocused(false);
            else setEndFocused(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitTypedDate(which);
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            height: 32,
            border: 0,
            outline: "none",
            background: "transparent",
            color: text,
            fontFamily: theme.fonts.regular,
            fontSize: 12,
            padding: 0,
          }}
        />
        <Icon iconName="Calendar" styles={{ root: { fontSize: 14 } }} />
      </div>
    </div>
  );

  const pillBaseStyle = {
    height: 40,
    border: `1px solid ${outline}`,
    borderRadius: 20,
    display: "inline-flex",
    alignItems: "center",
    background: "transparent",
    color: text,
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    whiteSpace: "nowrap",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <>
      {!enabled ? (
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={turnOn}
          aria-label="Turn on date filter"
          aria-pressed={false}
          style={{
            ...pillBaseStyle,
            padding: "0 12px",
            justifyContent: "center",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <Icon iconName="Calendar" styles={{ root: { fontSize: 16 } }} />
        </button>
      ) : (
        <div
          style={{
            ...pillBaseStyle,
            padding: 0,
            overflow: "hidden",
          }}
        >
          <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={openPicker}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={`Date filter: ${appliedLabel}`}
            aria-pressed
            style={{
              height: "100%",
              border: 0,
              padding: "0 10px 0 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "transparent",
              color: text,
              cursor: disabled ? "default" : "pointer",
              fontFamily: theme.fonts.regular,
              fontSize: 12,
            }}
          >
            <Icon iconName="Calendar" styles={{ root: { fontSize: 16 } }} />
            <span>{appliedLabel}</span>
            <Icon
              iconName={isOpen ? "ChevronUp" : "ChevronDown"}
              styles={{ root: { fontSize: 10 } }}
            />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={turnOff}
            aria-label="Turn off date filter"
            style={{
              height: "100%",
              border: 0,
              borderLeft: `1px solid ${outline}`,
              padding: "0 10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              color: text,
              cursor: disabled ? "default" : "pointer",
            }}
          >
            <Icon iconName="Cancel" styles={{ root: { fontSize: 10 } }} />
          </button>
        </div>
      )}

      {enabled && isOpen && (
        <Callout
          target={buttonRef.current}
          onDismiss={() => setIsOpen(false)}
          directionalHint={DirectionalHint.bottomRightEdge}
          gapSpace={8}
          isBeakVisible={false}
          setInitialFocus
          styles={{
            root: { zIndex: 1000000 },
            calloutMain: {
              width: 570,
              maxWidth: "calc(100vw - 24px)",
              borderRadius: 8,
              background,
              boxShadow: "0 4px 18px rgba(0,0,0,0.14)",
              overflow: "hidden",
            },
          }}
          role="dialog"
          ariaLabel="Select date range"
        >
          <div style={{ display: "flex", minHeight: 330 }}>
            <div
              style={{
                width: 130,
                flexShrink: 0,
                padding: "10px 0",
                borderRight: `1px solid ${theme.colors.addovation10Grey ?? "#ececec"}`,
              }}
            >
              {PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => selectPreset(preset)}
                  style={presetButtonStyle(draftPreset === preset.key)}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDraftPreset("custom")}
                style={{
                  ...presetButtonStyle(draftPreset === "custom"),
                  whiteSpace: "nowrap",
                }}
              >
                Custom range
              </button>
            </div>

            <div style={{ flex: 1, minWidth: 0, padding: "14px 14px 12px" }}>
              <div style={{ display: "flex", gap: 18, marginBottom: 5 }}>
                {dateField("From", "start", startText)}
                {dateField("To", "end", endText)}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 4,
                }}
              >
                <Calendar
                  value={draftStart ?? undefined}
                  onSelectDate={selectStart}
                  dateRangeType={DateRangeType.Day}
                  showGoToToday={false}
                  isMonthPickerVisible
                  showMonthPickerAsOverlay
                  highlightCurrentMonth
                  highlightSelectedMonth
                  styles={{ root: { width: "100%" } }}
                />
                <Calendar
                  value={draftEnd ?? undefined}
                  onSelectDate={selectEnd}
                  dateRangeType={DateRangeType.Day}
                  showGoToToday={false}
                  isMonthPickerVisible
                  showMonthPickerAsOverlay
                  highlightCurrentMonth
                  highlightSelectedMonth
                  styles={{ root: { width: "100%" } }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={clearDraft}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: text,
                    cursor: "pointer",
                    fontFamily: theme.fonts.regular,
                    fontSize: 12,
                    padding: "8px 4px",
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={apply}
                  style={{
                    minWidth: 58,
                    height: 34,
                    border: 0,
                    borderRadius: 5,
                    background: green,
                    color: "#ffffff",
                    cursor: "pointer",
                    fontFamily: theme.fonts.regular,
                    fontSize: 12,
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Callout>
      )}
    </>
  );
}
